import { Link, NavLink, useNavigate } from "react-router-dom";
import { Activity, LogOut, LayoutDashboard, Shield } from "lucide-react";
import { useAuth } from "@/lib/auth";

/**
 * Header — global navbar.
 *
 * Members see the live-market entry link:
 *   MARKET   → /market   (live Binance data · chart-first)
 */
export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";

  const onLogout = async () => { await logout(); navigate("/"); };

  const navBase =
    "inline-flex items-center gap-1.5 px-3 py-1.5 border transition-colors";
  const navIdle = "text-neutral-400 hover:text-white border-neutral-800 hover:border-neutral-600";
  const navActive = "text-[#00E5FF] border-[#00E5FF]/50 bg-[#00E5FF]/[0.06]";

  return (
    <header className="border-b border-neutral-900">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between gap-3">
        <Link to="/" data-testid="header-home" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
          <div className="w-8 h-8 border border-neutral-700 flex items-center justify-center bg-neutral-950">
            <Activity className="w-4 h-4 text-[#00E5FF]" />
          </div>
          <div className="leading-tight">
            <div className="font-black tracking-tight text-base sm:text-lg">CHARTLENZ</div>
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-500">
              chart intelligence terminal
            </div>
          </div>
        </Link>

        <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em]">
          {user ? (
            <>
              {user.email_verified !== false && (<>
              {/* Analysis flow nav — live market entry point. */}
              <NavLink
                to="/market"
                data-testid="header-market"
                title="Live market analysis on Binance data"
                className={({ isActive }) => `${navBase} ${isActive ? navActive : navIdle}`}
              >
                <Activity className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">market</span>
              </NavLink>

              {isAdmin && (
                <Link to="/admin" data-testid="header-admin"
                  className="inline-flex items-center gap-1.5 text-[#FFB000] hover:text-white px-3 py-1.5 border border-[#FFB000]/40 hover:border-white transition-colors">
                  <Shield className="w-3.5 h-3.5" /> admin
                </Link>
              )}
              <Link to="/dashboard" data-testid="header-dashboard"
                className="hidden md:inline-flex items-center gap-1.5 text-neutral-400 hover:text-white px-3 py-1.5 border border-neutral-800 hover:border-neutral-600 transition-colors">
                <LayoutDashboard className="w-3.5 h-3.5" /> dashboard
              </Link>
              </>)}
              <span className="hidden lg:inline text-neutral-600 normal-case tracking-normal text-xs">
                {user.email}
              </span>
              <button onClick={onLogout} data-testid="header-logout"
                className="inline-flex items-center gap-1.5 text-neutral-400 hover:text-white px-3 py-1.5 border border-neutral-800 hover:border-neutral-600 transition-colors">
                <LogOut className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">logout</span>
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="header-login"
                className="text-neutral-400 hover:text-white px-3 py-1.5 border border-neutral-800 hover:border-neutral-600 transition-colors">
                log in
              </Link>
              <Link to="/signup" data-testid="header-signup"
                className="text-black bg-white hover:bg-neutral-200 px-3 py-1.5 transition-colors font-bold">
                sign up
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
